import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { theme } from "./theme";
import {
  AtmosphericParticles,
  DynamicSubtitle,
  FilmGrain,
  SpecularShine,
} from "./components";

export const KyuStoreMetaHirara: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Helper spring shortcut (Remotion Pro pattern)
  const sp = (delay: number, damping = 200, stiffness = 120) =>
    spring({
      frame,
      fps,
      delay,
      config: { damping, stiffness },
    });

  // Dynamic Audio Ducking: ducks BGM while Orus speaks (frames 0 to 1260)
  const bgmVolume = (f: number) => {
    const fadeIn = interpolate(f, [0, 25], [0, 1], { extrapolateRight: "clamp" });
    const fadeOut = interpolate(f, [durationInFrames - 30, durationInFrames], [1, 0], {
      extrapolateLeft: "clamp",
    });
    const duck = f < 1250 ? 0.12 : 0.45;
    return duck * Math.min(fadeIn, fadeOut);
  };

  // Subtitles pinned strictly at safe band bottom: 400px (Y: 1520px)
  const SUBTITLES = [
    { from: 0, to: 98, text: "Mau push rank ke Mythic tapi bintangmu malah jalan di tempat?", highlight: "Mau push rank ke Mythic" },
    { from: 98, to: 215, text: "Nih, hero meta paling haram bulan ini yang wajib kamu pick!", highlight: "hero meta paling haram" },
    { from: 215, to: 425, text: "Pertama: Hirara! Hero assassin baru bertema dualitas yang damage burst-nya gila dan lagi viral di sosmed!", highlight: "Hirara! Hero assassin baru" },
    { from: 425, to: 665, text: "Kedua: Masha! Win rate tertinggi enam puluh persen, tiga bar darah tebal, sekali cakar turret dan hero musuh rata!", highlight: "Masha! Win rate tertinggi" },
    { from: 665, to: 860, text: "Ketiga: Hayabusa! Shadow kill-nya gak bisa ditarget, sekali kombo bayangan, core lawan langsung lenyap!", highlight: "Hayabusa! Shadow kill-nya" },
    { from: 860, to: 1045, text: "Biar main hero meta makin pede, bungkus skin favoritmu pakai diamond resmi dari Kyu Store!", highlight: "bungkus skin favoritmu" },
    { from: 1045, to: 1180, text: "Cukup User ID, bayar instan pakai QRIS, nol koma delapan detik masuk dan anti-minus seratus persen.", highlight: "nol koma delapan detik masuk" },
    { from: 1180, to: 1320, text: "Buka kyustore.com sekarang juga!", highlight: "kyustore.com" },
  ];

  // 1. Continuous Organic Camera Drift (3d-and-cinematic.md §3)
  const driftY = Math.sin(frame / 34) * 2.6;
  const driftX = Math.cos(frame / 40) * 1.8;

  // Camera rotations between scenes
  const camSceneRotY = interpolate(
    frame,
    [0, 215, 425, 665, 860, 1100, 1320],
    [-4, 5, -5, 4, -4, 4, 0],
    { easing: theme.ease }
  );
  const camSceneRotX = interpolate(
    frame,
    [0, 215, 425, 665, 860, 1100, 1320],
    [5, 2, 4, 2, 4, 2, 0],
    { easing: theme.ease }
  );

  const totalRotY = camSceneRotY + driftY;
  const totalRotX = camSceneRotX + driftX;

  // 2. Impact Shake & Zoom Punch on Act Entrances (Frames 218-226 and 1060-1068)
  const isHiraraImpact = frame >= 218 && frame <= 226;
  const isCtaImpact = frame >= 1060 && frame <= 1068;
  const isImpact = isHiraraImpact || isCtaImpact;

  const impactShakeX = isImpact ? (frame % 2 === 0 ? 8 : -8) : 0;
  const impactShakeY = isImpact ? (frame % 3 === 0 ? -5 : 5) : 0;
  const zoomPunch = interpolate(frame, [216, 219, 230], [1, 1.06, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: theme.ease,
  });

  // 3. Anticipation Dip on CTA (Frames 1160 - 1180)
  const ctaSpring = sp(1160, 200);
  const ctaDip = interpolate(frame, [1155, 1160, 1172], [0, 8, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const ctaPulse = Math.sin((frame - 1180) * 0.18) * 0.03 + 1.0;

  // 4. Specular Shine
  const shineProgress = interpolate(frame % 200, [0, 55], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: theme.ease,
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: theme.colors.bg,
        fontFamily: theme.fonts.headline,
        color: theme.colors.text,
        overflow: "hidden",
      }}
    >
      {/* Procedural Film Grain Overlay (4.5% SVG feTurbulence) */}
      <FilmGrain opacity={0.045} />

      {/* Audio Engine with Pro Dynamic Ducking */}
      <Audio src={staticFile("kyustore_meta_hirara_mastered.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={bgmVolume} />

      {/* Layered SFX Cues Synced per Frame */}
      {frame === 4 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.65} />}
      {frame === 100 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.7} />}

      {frame === 216 && <Audio src={staticFile("whoosh.mp3")} volume={0.8} />}
      {frame === 220 && <Audio src={staticFile("ui-success.mp3")} volume={0.85} />}
      {frame === 260 && <Audio src={staticFile("chime.mp3")} volume={0.75} />}

      {frame === 425 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.75} />}
      {frame === 460 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}

      {frame === 665 && <Audio src={staticFile("whoosh.mp3")} volume={0.75} />}
      {frame === 700 && <Audio src={staticFile("pop.mp3")} volume={0.7} />}

      {frame === 860 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.75} />}
      {frame === 1060 && <Audio src={staticFile("ui-success.mp3")} volume={0.9} />}
      {frame === 1165 && <Audio src={staticFile("click.mp3")} volume={0.8} />}

      {/* Background Subtle Tech Grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            radial-gradient(rgba(93, 63, 211, 0.08) 1.5px, transparent 1.5px),
            linear-gradient(to right, rgba(93, 63, 211, 0.035) 1px, transparent 1px)
          `,
          backgroundSize: "32px 32px, 64px 64px",
          pointerEvents: "none",
        }}
      />

      {/* Ambient Radial Spotlight */}
      <div
        style={{
          position: "absolute",
          top: "45%",
          left: "50%",
          width: 900,
          height: 900,
          transform: "translate(-50%, -50%)",
          background: "radial-gradient(circle, rgba(93, 63, 211, 0.14) 0%, rgba(248, 246, 254, 0) 70%)",
          filter: "blur(90px)",
          pointerEvents: "none",
        }}
      />

      {/* Top Header HUD Bar */}
      <div
        style={{
          position: "absolute",
          top: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Img src={staticFile("kyu_logo.png")} style={{ height: 48, objectFit: "contain" }} />
          <div style={{ width: 2, height: 26, backgroundColor: "#CBD5E1" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.12em",
              color: theme.colors.muted,
            }}
          >
            [ MLBB META REPORT OKTOBER 2026 ]
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: theme.colors.surface,
            border: `2px solid ${theme.colors.success}`,
            padding: "8px 22px",
            borderRadius: 24,
            boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
          }}
        >
          <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: theme.colors.success, boxShadow: "0 0 10px #10B981" }} />
          <span
            style={{
              fontFamily: theme.fonts.technical,
              fontSize: 18,
              fontWeight: 700,
              color: theme.colors.success,
              letterSpacing: "0.08em",
            }}
          >
            RANKED TIER SS & S+
          </span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 3D PERSPECTIVE VIEWPORT (1400px) with IMPACT SHAKE & ZOOM PUNCH           */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          perspective: 1400,
          perspectiveOrigin: "50% 48%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transform: `translate(${impactShakeX}px, ${impactShakeY}px) scale(${zoomPunch})`,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "relative",
            width: 900,
            height: 1100,
            transformStyle: "preserve-3d",
            transform: `rotateY(${totalRotY}deg) rotateX(${totalRotX}deg)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >

          {/* ===================================================================== */}
          {/* ACT 1: THE META HOOK (Frames 0 - 215)                                 */}
          {/* ===================================================================== */}
          {frame < 220 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [200, 218], [1, 0], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: "#FEE2E2",
                  border: "2px solid #EF4444",
                  borderRadius: 24,
                  padding: "8px 28px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 16,
                  boxShadow: "0 6px 20px rgba(239, 68, 68, 0.18)",
                  transform: `scale(${interpolate(sp(4, 200), [0, 1], [0.8, 1])})`,
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="3">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 800, color: "#B91C1C", letterSpacing: "0.08em" }}>
                  SOLO RANK AUTO GENDONG
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 74,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                  transform: `scale(${interpolate(sp(12, 200), [0, 1], [0.85, 1])})`,
                }}
              >
                HERO META PALING HARAM <br />
                <span style={{ color: theme.colors.accent }}>BULAN INI DI MOBILE LEGENDS!</span>
              </h1>

              {/* Master 3D Hook Stage with Mythical Glory Dragon Badge */}
              <div
                style={{
                  position: "relative",
                  width: 840,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "36px 40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(sp(20, 200), [0, 1], [0.88, 1])})`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Big Mythical Glory Badge */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 24,
                    transform: "translateZ(55px)",
                    marginBottom: 20,
                  }}
                >
                  <Img
                    src={staticFile("official_rank_mythical_glory.png")}
                    style={{
                      width: 140,
                      height: 140,
                      objectFit: "contain",
                      filter: "drop-shadow(0 15px 30px rgba(255, 176, 32, 0.4))",
                    }}
                  />
                  <div>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.muted }}>
                      TARGET RANK MUSIM INI
                    </div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 52, color: theme.colors.text, lineHeight: 0.95 }}>
                      MYTHICAL GLORY 100★
                    </div>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 800, color: theme.colors.success, marginTop: 4 }}>
                      WAJIB PICK ATAU BAN!
                    </div>
                  </div>
                </div>

                {/* 3 Meta Hero Teaser Pills */}
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    gap: 16,
                    transform: "translateZ(35px)",
                  }}
                >
                  <div style={{ flex: 1, backgroundColor: theme.colors.surface, border: "2px solid #E2E8F0", borderRadius: 18, padding: "12px 16px", textAlign: "center" }}>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 700, color: theme.colors.accent }}>#1 ASSASSIN</div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>HIRARA</div>
                  </div>
                  <div style={{ flex: 1, backgroundColor: theme.colors.surface, border: "2px solid #E2E8F0", borderRadius: 18, padding: "12px 16px", textAlign: "center" }}>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 700, color: theme.colors.success }}>#2 FIGHTER</div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>MASHA</div>
                  </div>
                  <div style={{ flex: 1, backgroundColor: theme.colors.surface, border: "2px solid #E2E8F0", borderRadius: 18, padding: "12px 16px", textAlign: "center" }}>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 700, color: theme.colors.gold }}>#3 JUNGLE</div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>HAYABUSA</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 2: HERO #1 — HIRARA (Frames 215 - 425)                           */}
          {/* Featuring the Authentic Clean Transparent Cutout Hirara.png           */}
          {/* ===================================================================== */}
          {frame >= 210 && frame < 430 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [210, 225, 410, 428],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.accentLight,
                  border: `2px solid ${theme.colors.accent}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(93, 63, 211, 0.18)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.accent} strokeWidth="3">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.accent, letterSpacing: "0.08em" }}>
                  HERO META #1 • TIER S+ ASSASSIN
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                HIRARA — <span style={{ color: theme.colors.accent }}>ASSASSIN DUALITAS BURST DAMAGE</span>
              </h1>

              {/* Asymmetrical Split Stage with Hirara Cutout Breaking Bounds */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "24px 32px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 20,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Left Side: Authentic Clean Transparent Cutout Hirara */}
                <div
                  style={{
                    flex: "0 0 400px",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translateZ(65px)",
                    position: "relative",
                  }}
                >
                  <Img
                    src={staticFile("heroes/Hirara.png")}
                    style={{
                      width: 440,
                      height: 480,
                      objectFit: "contain",
                      filter: "drop-shadow(0 20px 35px rgba(93, 63, 211, 0.35))",
                    }}
                  />
                  {/* Floating New Hero Badge */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 10,
                      backgroundColor: "rgba(18, 10, 46, 0.92)",
                      border: "1.5px solid #00F0FF",
                      color: "#FFFFFF",
                      padding: "6px 16px",
                      borderRadius: 14,
                      fontFamily: theme.fonts.technical,
                      fontSize: 14,
                      fontWeight: 800,
                      boxShadow: "0 4px 15px rgba(0, 240, 255, 0.3)",
                    }}
                  >
                    NEW HERO VIRAL SOSMED
                  </div>
                </div>

                {/* Right Side: Tactical Attributes */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                    transform: "translateZ(35px)",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: `2px solid ${theme.colors.accent}`,
                      padding: "16px 20px",
                      boxShadow: "0 6px 20px rgba(93, 63, 211, 0.12)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.accent }}>
                      KOMBO DUALITAS VOID & CAHAYA
                    </div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                      BURST DAMAGE INSTAN MEMATIKAN
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: "2px solid #E2E8F0",
                      padding: "16px 20px",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.success }}>
                      STATUS META RANKED
                    </div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                      AUTO FIRST PICK / PERMABAN
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.darkBadge,
                      color: "#FFFFFF",
                      borderRadius: 20,
                      padding: "14px 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      boxShadow: "0 10px 25px rgba(30, 18, 74, 0.3)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 24 }}>
                      COUNTER HERO LATE GAME
                    </div>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 800, color: "#10B981" }}>
                      TIER S+
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 3: HERO #2 — MASHA (Frames 425 - 665)                             */}
          {/* Featuring Masha.png & Record High Win Rate 60.3%                      */}
          {/* ===================================================================== */}
          {frame >= 420 && frame < 670 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [420, 435, 650, 668],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.successLight,
                  border: `2px solid ${theme.colors.success}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                  HERO META #2 • WIN RATE TERTINGGI 60.3%
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                MASHA — <span style={{ color: theme.colors.success }}>TUKANG RATAKAN TURRET & HERO</span>
              </h1>

              {/* Asymmetrical Split Stage with Masha Cutout */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "24px 32px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 20,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Left Side: Authentic Clean Transparent Cutout Masha */}
                <div
                  style={{
                    flex: "0 0 380px",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translateZ(65px)",
                    position: "relative",
                  }}
                >
                  <Img
                    src={staticFile("heroes/Masha.png")}
                    style={{
                      width: 420,
                      height: 480,
                      objectFit: "contain",
                      filter: "drop-shadow(0 20px 35px rgba(255, 176, 32, 0.35))",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 10,
                      backgroundColor: theme.colors.success,
                      color: "#FFFFFF",
                      padding: "6px 16px",
                      borderRadius: 14,
                      fontFamily: theme.fonts.display,
                      fontSize: 22,
                      letterSpacing: "0.04em",
                      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.4)",
                    }}
                  >
                    60.3% WIN RATE RESMI
                  </div>
                </div>

                {/* Right Side: Masha Power Stats */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                    transform: "translateZ(35px)",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: `2px solid ${theme.colors.success}`,
                      padding: "16px 20px",
                      boxShadow: "0 6px 20px rgba(16, 185, 129, 0.12)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.success }}>
                      KETAHANAN HP LUAR BIASA
                    </div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                      TIGA BAR DARAH TEBAL (ANTI-BURST)
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: "2px solid #E2E8F0",
                      padding: "16px 20px",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.accent }}>
                      OBJEKTIF UTAMA TEAMFIGHT
                    </div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                      SPESIALIS BACKDOOR & CURl TURRET
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.darkBadge,
                      color: "#FFFFFF",
                      borderRadius: 20,
                      padding: "14px 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      boxShadow: "0 10px 25px rgba(30, 18, 74, 0.3)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 24 }}>
                      RANKING 1 WIN RATE MLBB
                    </div>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 800, color: "#00F0FF" }}>
                      TIER SS
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 4: HERO #3 — HAYABUSA (Frames 665 - 860)                          */}
          {/* Featuring Hayabusa.png & Sky Piercer Combo                            */}
          {/* ===================================================================== */}
          {frame >= 660 && frame < 865 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(
                  frame,
                  [660, 675, 845, 863],
                  [0, 1, 1, 0],
                  { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
                ),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.goldLight,
                  border: `2px solid ${theme.colors.gold}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(255, 176, 32, 0.2)",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2.5">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: "#92400E", letterSpacing: "0.08em" }}>
                  HERO META #3 • RAJA ASSASSIN JUNGLE
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 72,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 20,
                }}
              >
                HAYABUSA — <span style={{ color: theme.colors.accent }}>BAYANGAN ANTI-TARGET MEMATIKAN</span>
              </h1>

              {/* Asymmetrical Split Stage with Hayabusa Cutout */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 520,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "24px 32px",
                  transformStyle: "preserve-3d",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 20,
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Left Side: Authentic Clean Transparent Cutout Hayabusa */}
                <div
                  style={{
                    flex: "0 0 380px",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: "translateZ(65px)",
                    position: "relative",
                  }}
                >
                  <Img
                    src={staticFile("heroes/Hayabusa.png")}
                    style={{
                      width: 420,
                      height: 480,
                      objectFit: "contain",
                      filter: "drop-shadow(0 20px 35px rgba(93, 63, 211, 0.35))",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 10,
                      backgroundColor: "rgba(18, 10, 46, 0.92)",
                      border: "1.5px solid #FFB020",
                      color: "#FFFFFF",
                      padding: "6px 16px",
                      borderRadius: 14,
                      fontFamily: theme.fonts.technical,
                      fontSize: 14,
                      fontWeight: 800,
                      boxShadow: "0 4px 15px rgba(255, 176, 32, 0.3)",
                    }}
                  >
                    LANGGANAN BAN TERTINGGI
                  </div>
                </div>

                {/* Right Side: Hayabusa Shadow Tactics */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                    transform: "translateZ(35px)",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: `2px solid ${theme.colors.accent}`,
                      padding: "16px 20px",
                      boxShadow: "0 6px 20px rgba(93, 63, 211, 0.12)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.accent }}>
                      ULTIMATE: OUGI SHADOW KILL
                    </div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                      TIDAK BISA DITARGET MUSUH
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderRadius: 20,
                      border: "2px solid #E2E8F0",
                      padding: "16px 20px",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.gold }}>
                      KOMBINASI ITEM REKOMENDASI
                    </div>
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 28, color: theme.colors.text }}>
                      SKY PIERCER + HUNTER STRIKE
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: theme.colors.darkBadge,
                      color: "#FFFFFF",
                      borderRadius: 20,
                      padding: "14px 20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      boxShadow: "0 10px 25px rgba(30, 18, 74, 0.3)",
                    }}
                  >
                    <div style={{ fontFamily: theme.fonts.display, fontSize: 24 }}>
                      KONTROL MAP & OBJEKTIF
                    </div>
                    <div style={{ fontFamily: theme.fonts.technical, fontSize: 14, fontWeight: 800, color: "#10B981" }}>
                      TIER S+
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===================================================================== */}
          {/* ACT 5: GRAND CLIMAX & SEAMLESS KYU STORE CTA (Frames 860 - 1320)      */}
          {/* Features Colossal 0.82s Tabular-Nums, Weekly Pass & Anticipation Dip   */}
          {/* ===================================================================== */}
          {frame >= 855 && (
            <div
              style={{
                position: "absolute",
                transformStyle: "preserve-3d",
                opacity: interpolate(frame, [855, 875], [0, 1], { extrapolateLeft: "clamp" }),
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 20,
              }}
            >
              <div
                style={{
                  backgroundColor: theme.colors.successLight,
                  border: `2px solid ${theme.colors.success}`,
                  padding: "6px 24px",
                  borderRadius: 22,
                  marginBottom: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  boxShadow: "0 4px 15px rgba(16, 185, 129, 0.15)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.08em" }}>
                  BUNGKUS SKIN HERO META FAVORITMU
                </span>
              </div>

              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 74,
                  textAlign: "center",
                  lineHeight: 1.0,
                  marginBottom: 24,
                }}
              >
                TOP UP RESMI & AMAN <span style={{ color: theme.colors.accent }}>DI KYUSTORE.COM</span>
              </h1>

              {/* Master CTA Card with Anticipation Dip */}
              <div
                style={{
                  position: "relative",
                  width: 860,
                  height: 540,
                  backgroundColor: theme.colors.surfaceSubtle,
                  backdropFilter: "blur(20px)",
                  borderRadius: 38,
                  border: `3px solid ${theme.colors.accent}`,
                  boxShadow: "0 35px 85px rgba(93, 63, 211, 0.22), 0 10px 25px rgba(0,0,0,0.05)",
                  padding: "36px 40px",
                  transformStyle: "preserve-3d",
                  transform: `scale(${interpolate(ctaSpring, [0, 1], [0.85, 1])}) translateY(${ctaDip}px)`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SpecularShine progress={shineProgress} angle={28} coreWidth={90} haloWidth={260} borderRadius={38} />

                {/* Big Kyu Store Brand Unit */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 20,
                    marginBottom: 22,
                    transform: "translateZ(35px)",
                  }}
                >
                  <Img
                    src={staticFile("kyu_logo.png")}
                    style={{
                      height: 100,
                      objectFit: "contain",
                      filter: "drop-shadow(0 12px 28px rgba(93, 63, 211, 0.28))",
                    }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontFamily: theme.fonts.display, fontSize: 54, color: theme.colors.text, lineHeight: 0.95 }}>
                      KYU STORE INDONESIA
                    </span>
                    <span style={{ fontFamily: theme.fonts.technical, fontSize: 18, fontWeight: 700, color: theme.colors.success, letterSpacing: "0.1em" }}>
                      DISTRIBUTOR RESMI MOONTON DIRECT API
                    </span>
                  </div>
                </div>

                {/* Dual Offer Badges: 0.82s Inject + Weekly Pass Rp 25.000 */}
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    gap: 20,
                    marginBottom: 22,
                    transform: "translateZ(45px)",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.surface,
                      border: "2px solid #E2E8F0",
                      borderRadius: 20,
                      padding: "14px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
                    }}
                  >
                    <Img src={staticFile("official_weekly_pass_clean.png")} style={{ width: 56, height: 56, objectFit: "contain" }} />
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: theme.colors.muted }}>
                        WEEKLY DIAMOND PASS
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 32, color: theme.colors.accent }}>
                        RP 25.000 (HEMAT 70%)
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      flex: 1,
                      backgroundColor: theme.colors.darkBadge,
                      color: "#FFFFFF",
                      borderRadius: 20,
                      padding: "14px 20px",
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      boxShadow: "0 10px 25px rgba(30, 18, 74, 0.35)",
                    }}
                  >
                    <Img src={staticFile("diamond_3d_crystal.png")} style={{ width: 48, height: 48, objectFit: "contain" }} />
                    <div>
                      <div style={{ fontFamily: theme.fonts.technical, fontSize: 13, fontWeight: 700, color: "#00F0FF" }}>
                        PROSES TRANSAKSI
                      </div>
                      <div style={{ fontFamily: theme.fonts.display, fontSize: 32, color: theme.colors.success }}>
                        0.82 DETIK SATSET
                      </div>
                    </div>
                  </div>
                </div>

                {/* Big Glowing CTA Button with Pulse */}
                <div
                  style={{
                    backgroundColor: theme.colors.success,
                    color: "#FFFFFF",
                    padding: "20px 52px",
                    borderRadius: 26,
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    boxShadow: "0 18px 45px rgba(16, 185, 129, 0.38)",
                    transform: `translateZ(55px) scale(${ctaPulse})`,
                    marginBottom: 20,
                  }}
                >
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span style={{ fontFamily: theme.fonts.display, fontSize: 40, letterSpacing: "0.04em" }}>
                    TOP UP SEKARANG: KYUSTORE.COM
                  </span>
                </div>

                {/* Trust Points */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 32,
                    fontFamily: theme.fonts.headline,
                    fontSize: 20,
                    fontWeight: 800,
                    color: theme.colors.muted,
                    transform: "translateZ(30px)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>100% Anti-Minus</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Tanpa Password</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Cukup User ID & Zone</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 18 Atmospheric Floating Light Motes */}
          <AtmosphericParticles frame={frame} count={18} />
        </div>
      </div>

      {/* ========================================================================= */}
      {/* FOOTER TICKER (Safe Zone Bottom 80px)                                     */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 80,
          left: theme.spacing.page,
          right: theme.spacing.page,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: theme.colors.surface,
          border: `2px solid ${theme.colors.border}`,
          borderRadius: 22,
          padding: "14px 30px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.04)",
          zIndex: 50,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: theme.fonts.headline, fontSize: 18, fontWeight: 800, color: theme.colors.muted }}>
          <span style={{ color: theme.colors.accent }}>METODE RESMI:</span>
          <span>QRIS • BCA • MANDIRI • BRI • DANA • GOPAY • SHOPEEPAY</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: theme.fonts.technical, fontSize: 16, fontWeight: 700, color: theme.colors.success }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.colors.success} strokeWidth="3">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>AUTO INJECT ACTIVE 24/7</span>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: PINNED AT OPTIMAL SAFE BAND (bottom: 400px)            */}
      {/* ========================================================================= */}
      <DynamicSubtitle frame={frame} subtitles={SUBTITLES} bottom={400} />
    </AbsoluteFill>
  );
};
